import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`w-4 h-4 ${i <= Math.round(rating) ? 'fill-red-600 text-red-600' : 'text-zinc-300'}`}
      />
    );
  }
  return <div className="flex gap-0.5">{stars}</div>;
}

function YelpReviewCard({ review }) {
  return (
    <div className="border border-zinc-200 bg-white p-5" data-testid={`yelp-review-${review.user}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-red-600 text-white flex items-center justify-center font-bold text-sm rounded-full">
          {review.user.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="font-heading text-xs font-bold uppercase tracking-tight">{review.user}</p>
          <StarRating rating={review.rating} />
        </div>
      </div>
      <p className="font-body text-sm text-zinc-600 leading-relaxed line-clamp-4">{review.text}</p>
      {review.url && (
        <a href={review.url} target="_blank" rel="noopener noreferrer"
          className="inline-block mt-3 font-body text-xs text-red-600 hover:underline">
          Read on Yelp &rarr;
        </a>
      )}
    </div>
  );
}

export default function YelpBadge() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchYelp = async () => {
      try {
        const res = await axios.get(`${API}/yelp/business`);
        if (res.data && res.data.review_count > 0) {
          setData(res.data);
        }
      } catch {
        // Silently fail — Yelp section is optional
      }
    };
    fetchYelp();
  }, []);

  // Don't render anything if no Yelp reviews
  if (!data || data.review_count === 0) return null;

  return (
    <section className="py-16 px-6 bg-white border-t border-zinc-200" data-testid="yelp-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-4">
            <svg viewBox="0 0 40 40" className="w-10 h-10 flex-shrink-0" aria-label="Yelp">
              <rect width="40" height="40" rx="8" fill="#D32323" />
              <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fill="white" fontWeight="bold" fontSize="18">Y</text>
            </svg>
            <div>
              <div className="flex items-center gap-2">
                <StarRating rating={data.rating} />
                <span className="font-heading text-lg font-bold">{data.rating}</span>
              </div>
              <p className="font-body text-xs text-zinc-500">
                {data.review_count} review{data.review_count !== 1 ? 's' : ''} on Yelp
              </p>
            </div>
          </div>
          {data.url && (
            <a href={data.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-red-600 text-white font-heading font-bold text-xs uppercase tracking-wider px-5 py-2.5 hover:-translate-y-0.5 transition-transform"
              data-testid="yelp-review-link">
              Review Us on Yelp
            </a>
          )}
        </div>

        {data.reviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.reviews.map((review, i) => (
              <YelpReviewCard key={i} review={review} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
